// Link to calc spreadsheet:
// static/Water_PVT_Schtuff.xlsx
import {
  Water_Density_Options_Enum,
  Water_Solution_Gas_Options_Enum,
  Water_Volume_Factor_Options_Enum
} from "gql/api";
import { waterPhaseVolumeFactorCalculator as jones } from "./jones";
import { waterPhaseVolumeFactorCalculator as gould } from "./gould";
import { waterPhaseVolumeFactorCalculator as mccain } from "./mccain";
import { waterPhaseVolumeFactorCalculator as spiveyEtAl } from "./spiveyEtAl";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { evaluateLagrangePolynomialWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

const methodMapBw = {
  [Water_Volume_Factor_Options_Enum.Jones]: jones,
  [Water_Volume_Factor_Options_Enum.Gould]: gould,
  [Water_Volume_Factor_Options_Enum.Mccain]: mccain,
  [Water_Volume_Factor_Options_Enum.Spivey]: spiveyEtAl
};

const methodMapRsw = {
  [Water_Volume_Factor_Options_Enum.Jones]: jones,
  [Water_Volume_Factor_Options_Enum.Gould]: gould,
  [Water_Volume_Factor_Options_Enum.Mccain]: mccain,
  [Water_Volume_Factor_Options_Enum.Spivey]: spiveyEtAl
};

const methodMapPw = {
  [Water_Volume_Factor_Options_Enum.Jones]: jones,
  [Water_Volume_Factor_Options_Enum.Gould]: gould,
  [Water_Volume_Factor_Options_Enum.Mccain]: mccain,
  [Water_Volume_Factor_Options_Enum.Spivey]: spiveyEtAl
};

export function getWaterCalculations(
  inSituPressurePSIA: number,
  temperatureF: number,
  bubblePointPressurePSIA: number,
  salinity_ppm: number,
  oilAPI: number,
  waterSpecificGravity: number,
  gasSpecificGravity: number,
  gasMolecularWeight: number,
  gasCompressibilityFactor: number,
  waterVolumeFactorCorrelation: Water_Volume_Factor_Options_Enum,
  waterSolutionGasCorrelation: Water_Solution_Gas_Options_Enum,
  waterDensityCorrelation: Water_Density_Options_Enum,
  gasOilRatioSCFSTB: number,
  oilPhaseGasSolubilitySCFSTB: number,
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  gasLiquidRatioSCFSTB = 0,
  oilPhaseGasSolubilityCorrectedSCFSTB = 0,
  waterCutRatio = 0
) {
  const waterVolumeFactorMethod =
    waterVolumeFactorCorrelation || Water_Volume_Factor_Options_Enum.Jones;
  const waterSolutionGasMethod =
    waterSolutionGasCorrelation || Water_Solution_Gas_Options_Enum.Mccain;
  const waterDensityMethod =
    waterDensityCorrelation || Water_Density_Options_Enum.Gould;

  const params = {
    inSituPressurePSIA,
    temperatureF,
    bubblePointPressurePSIA,
    salinity_ppm,
    oilAPI,
    waterSpecificGravity,
    gasSpecificGravity,
    gasMolecularWeight,
    gasCompressibilityFactor,
    gasOilRatioSCFSTB,
    oilPhaseGasSolubilitySCFSTB
  };

  const bwCalculator =
    methodMapBw[waterVolumeFactorMethod].waterPhaseVolumeFactorBwCalculator;
  const rswCalculator =
    methodMapRsw[waterSolutionGasMethod].waterSolutionGasRswCalculator;
  const pwCalculator = methodMapPw[waterDensityMethod].waterDensityPwCalculator;

  let waterVolumeFactorBBLSTB: number = 0;
  let waterPhaseGasSolubilitySCFSTB: number = 0;

  if (customFluidProperties?.waterFormationVolumeFactorBBLSTB != null) {
    waterVolumeFactorBBLSTB = evaluateLagrangePolynomialWithGroupings(
      customFluidProperties.waterFormationVolumeFactorBBLSTB,
      inSituPressurePSIA,
      temperatureF
    );
  } else {
    waterVolumeFactorBBLSTB = bwCalculator({
      ...params
    }).waterVolumeFactorBBLSTB;
  }

  if (customFluidProperties?.solutionGasWaterRatioSCFSTB != null) {
    waterPhaseGasSolubilitySCFSTB = evaluateLagrangePolynomialWithGroupings(
      customFluidProperties.solutionGasWaterRatioSCFSTB,
      inSituPressurePSIA,
      temperatureF
    );
  } else {
    ({ waterPhaseGasSolubilitySCFSTB } = rswCalculator({
      ...params,
      waterVolumeFactorBBLSTB
    }));
  }

  // PVT!AA24 — in situ Rsw corrected for GLR:
  // IF(AA23-AA27>0, AN10*D46, 0)
  const waterPhaseGasSolubilityCorrectedSCFSTB =
    gasLiquidRatioSCFSTB - oilPhaseGasSolubilityCorrectedSCFSTB > 0
      ? waterPhaseGasSolubilitySCFSTB * waterCutRatio
      : 0;

  const { waterDensityLBMFT3 } = pwCalculator({
    ...params,
    waterVolumeFactorBBLSTB,
    waterPhaseGasSolubilitySCFSTB
  });

  return {
    waterDensityLBMFT3,
    waterVolumeFactorBBLSTB,
    waterPhaseGasSolubilitySCFSTB,
    waterPhaseGasSolubilityCorrectedSCFSTB
  };
}
